import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  FormInput,
  FormLabeledSwitch,
  FormSelectInput,
  FormTextarea,
} from "apps/portal/src/components/atoms";
import CustomTooltip from "apps/portal/src/components/atoms/CustomTooltip";
import { useFormikContext } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AppointableFormValues } from "../../types";
import { FormCategorySelect } from "./components/FormCategorySelect";
import {
  INTERVAL_ALIGNMENT_VALUES,
  selectOptions,
  selectOptionsBlockExtraTime,
} from "./constants";
import { formatIntervalOption } from "./utils/formatIntervalOption";

export const AppointableWizardDetailsForm = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<AppointableFormValues>();

  const bookingPossibleEveryOptions = useMemo(() => {
    const durationValue = values?.details?.duration?.value;
    if (!durationValue) return [];
    const result = INTERVAL_ALIGNMENT_VALUES.filter(
      (v) => v <= durationValue,
    ).map(formatIntervalOption);
    setFieldValue("details.bookingPossibleEvery", result[result.length - 1]);
    return result;
  }, [values?.details?.duration, setFieldValue]);

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
        <FormInput<AppointableFormValues>
          name="details.name"
          label={t(
            "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.name.label",
          )}
          placeholder={t(
            "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.name.placeholder",
          )}
          containerClassName="flex-1"
          isRequired
        />
        <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:gap-8">
          <FormCategorySelect />
          <div className="shadow-xs flex h-10 max-h-10 min-h-10 w-full flex-1 flex-row items-center justify-between rounded-[0.625rem] border border-gray-200 px-3 lg:mt-[1.625rem]">
            <div className="flex flex-row items-center gap-0.5">
              <div className="text-sm font-medium text-gray-950">
                {t(
                  "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.membersOnly.title",
                )}
              </div>
              <CustomTooltip
                message={t(
                  "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.membersOnly.tooltip",
                )}
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="size-4 text-gray-500"
                />
              </CustomTooltip>
            </div>
            <FormLabeledSwitch<AppointableFormValues> name="details.isMembersOnly" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
        <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:gap-8">
          <FormSelectInput<AppointableFormValues, number>
            options={selectOptions}
            name="details.duration"
            containerClassName="flex-1"
            label={t(
              "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.duration.label",
            )}
            isRequired
          />
          <FormSelectInput<AppointableFormValues, number>
            options={selectOptionsBlockExtraTime}
            name="details.blockExtraTime"
            containerClassName="flex-1"
            helpTooltip={t(
              "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.blockExtraTime.helpTooltip",
            )}
            label={t(
              "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.blockExtraTime.label",
            )}
          />
        </div>
        <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:gap-8">
          <FormSelectInput<AppointableFormValues, number>
            options={bookingPossibleEveryOptions}
            name="details.bookingPossibleEvery"
            containerClassName="flex-1"
            isRequired
            notFound={t(
              "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.bookingPossibleEvery.notFound",
            )}
            label={t(
              "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.bookingPossibleEvery.label",
            )}
          />
          <div className="shadow-xs flex h-10 max-h-10 min-h-10 w-full flex-1 flex-row items-center justify-between rounded-[0.625rem] border border-gray-200 px-3 lg:mt-[1.625rem]">
            <div className="flex flex-row items-center gap-0.5">
              <div className="text-sm font-medium text-gray-950">
                {t(
                  "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.topOfTheHour.title",
                )}
              </div>
              <CustomTooltip
                message={t(
                  "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.topOfTheHour.tooltip",
                )}
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="size-4 text-gray-500"
                />
              </CustomTooltip>
            </div>
            <FormLabeledSwitch<AppointableFormValues> name="details.isTopOfTheHour" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
        <FormTextarea<AppointableFormValues>
          name="details.description"
          containerClassName="flex-1"
          rows={5}
          isOptional
          placeholder={t(
            "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.description.placeholder",
          )}
          label={t(
            "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.description.label",
          )}
        />
        <FormTextarea<AppointableFormValues>
          name="details.appointmentTerms"
          rows={5}
          containerClassName="flex-1"
          isOptional
          placeholder={t(
            "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.appointmentTerms.placeholder",
          )}
          label={t(
            "page.settings.tab.appointments.appointableWizard.appointableWizardDetailsForm.appointmentTerms.label",
          )}
        />
      </div>
    </div>
  );
};
